import React, { useEffect, useRef, useState } from 'react';
import { addTargetBlank } from '../utils';
import useThrottle from './hooks/useThrottle';

const findNextSpace = (text, cursor) => {
	let i = cursor;
	while (i < text.length && text[i] !== ' ') {
		i++;
	}
	return i;
};

const STREAM_DELAY = 60;
const SCROLL_DELAY = 300;
const BOTTOM_GRACE_ZONE = 70;

const StreamingMessage = ({ message, className, style, chatWindowRef, setIsMessageStreaming }) => {
	const [text, setText] = useState(message.value);
	const [cursor, setCursor] = useState(0);
	const [displayedText, setDisplayedText] = useState('');
	const throttledText = useThrottle(displayedText, SCROLL_DELAY);
	const messageDivRef = useRef();
	const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
	const [isStreamingDone, setIsStreamingDone] = useState(false);

	useEffect(() => {
		setText(message.value);
	}, [message]);

	useEffect(() => {
		if (message.value === displayedText && message.isDone) {
			setIsStreamingDone(true);
			setIsMessageStreaming(false);
		} else {
			setIsMessageStreaming(true);
		}
	}, [message.isDone, message.value, displayedText]);

	useEffect(() => {
		if (cursor >= text.length) {
			return;
		}

		const timeout = setTimeout(() => {
			const nextIndex = findNextSpace(text, cursor);
			setCursor(nextIndex + 1);
			setDisplayedText(text.slice(0, nextIndex + 1));
		}, STREAM_DELAY);

		return () => clearTimeout(timeout);
	}, [text, cursor]);

	useEffect(() => {
		if (messageDivRef.current && isScrolledToBottom && !isStreamingDone) {
			messageDivRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
		}
	}, [throttledText, isScrolledToBottom, isStreamingDone]);

	useEffect(() => {
		if (isStreamingDone) return;

		const onScroll = () => {
			if (!messageDivRef.current) return;
			const cw = chatWindowRef.current;
			const isScrolledToBottom = cw.scrollHeight - cw.clientHeight <= cw.scrollTop + BOTTOM_GRACE_ZONE;
			setIsScrolledToBottom(isScrolledToBottom);
		};

		if (chatWindowRef.current) {
			chatWindowRef.current.addEventListener('scroll', onScroll);
		}

		return () => {
			if (chatWindowRef.current) {
				chatWindowRef.current.removeEventListener('scroll', onScroll);
			}
		};
	}, [isStreamingDone]);

	let fixedText = displayedText.replace(/\\/g, '"').replace(/""/g, '"');
	fixedText = addTargetBlank(fixedText);

	return (
		<p
			className={className}
			style={{ ...style, scrollMarginBottom: '100px' }}
			dangerouslySetInnerHTML={{ __html: fixedText }}
			ref={messageDivRef}
		/>
	);
};

export default StreamingMessage;
